import { useEffect, useState, useCallback } from "react";
import { Bell, Warning, ShieldCheck, Drop, Truck, CheckCircle } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/apiClient";
import { Link } from "react-router-dom";

const ICON_BY_TYPE = {
  tank_critical:    { icon: Drop, tone: "text-red-600 bg-red-50" },
  tank_warning:     { icon: Drop, tone: "text-amber-600 bg-amber-50" },
  pending_approval: { icon: ShieldCheck, tone: "text-amber-600 bg-amber-50" },
  critical_variance:{ icon: Warning, tone: "text-red-600 bg-red-50" },
  stale_vehicles:   { icon: Truck, tone: "text-amber-600 bg-amber-50" },
};

const LEVEL_DOT = {
  critical: "bg-red-500",
  warning:  "bg-amber-500",
  info:     "bg-sky-500",
};

export default function NotificationBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);

  const fetchNotifs = useCallback(() => {
    api.get("/notifications")
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Fetch sekali saat komponen mount (untuk badge awal).
    fetchNotifs();
    // Polling background dimatikan untuk mengurangi beban backend production —
    // notifikasi tetap fresh karena akan di-fetch ulang setiap kali user
    // membuka popover bell (lihat onOpenChange di Popover di bawah).
  }, [fetchNotifs]);

  const badgeCount = Math.min(data.unread || 0, 99);
  const hasUnread = badgeCount > 0;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchNotifs(); }}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-slate-100 transition-colors hidden sm:block"
          data-testid="bell-btn"
          aria-label="Notifikasi"
        >
          <Bell size={18} weight="duotone" className="text-slate-600" />
          {hasUnread && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none"
              data-testid="bell-badge"
            >
              {badgeCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0 overflow-hidden"
        data-testid="notif-popover"
      >
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div>
            <h4 className="font-heading text-sm font-bold text-slate-800">Notifikasi</h4>
            <p className="text-[10px] text-slate-500 mt-0.5">
              {hasUnread ? `${data.unread} peringatan aktif` : "Tidak ada peringatan"}
            </p>
          </div>
          <button
            onClick={fetchNotifs}
            className="text-[11px] text-sky-700 font-semibold hover:underline"
            data-testid="notif-refresh"
          >
            Refresh
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
          {(!data.items || data.items.length === 0) ? (
            <div className="px-4 py-10 text-center">
              <CheckCircle size={28} weight="duotone" className="mx-auto text-emerald-500 mb-2" />
              <p className="text-xs text-slate-500">Semua aman. Tidak ada notifikasi.</p>
            </div>
          ) : (
            data.items.map((n) => {
              const cfg = ICON_BY_TYPE[n.type] || { icon: Bell, tone: "text-slate-500 bg-slate-50" };
              const Icon = cfg.icon;
              return (
                <Link
                  key={n.id}
                  to={n.to || "#"}
                  onClick={() => setOpen(false)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className={`w-8 h-8 rounded-full ${cfg.tone} flex items-center justify-center shrink-0`}>
                    <Icon size={16} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${LEVEL_DOT[n.level] || "bg-slate-400"} shrink-0`} />
                      <div className="text-[12.5px] font-semibold text-slate-800 truncate">{n.title}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{n.message}</div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
